import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { CashHandover, CashLocation } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { EmptyState, TableSkeleton } from '../components/States';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { bogotaDay, formatDate, formatMoney } from '../lib/format';
import type { Language } from '../i18n';

/** Suma días a una fecha de Bogotá (AAAA-MM-DD). */
function shiftDay(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Vista "Caja": efectivo esperado por local, ventas por medio de pago, entregas de efectivo y base autorizada (Etapa 2). */
export function Cash() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { user, can } = useAuth();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const seesAll = can('cash.view');
  const places = user?.locations ?? [];
  const today = bogotaDay(new Date());

  // Quien no consulta todas las cajas (local, Bodega) siempre trabaja sobre una de sus ubicaciones.
  const [selected, setSelected] = useState<number | ''>('');
  const locationId = selected === '' ? (seesAll ? undefined : places[0]?.id) : Number(selected);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [range, setRange] = useState({ from: today, to: today });

  const ready = !!user && (seesAll || locationId != null);
  const summary = useLoad(() => (ready ? api.cashSummary({ location_id: locationId, from: range.from, to: range.to }) : Promise.resolve(null)), [ready, locationId, range.from, range.to]);
  const handovers = useLoad(() => (ready ? api.cashHandovers({ location_id: locationId, from: range.from, to: range.to, limit: 100 }) : Promise.resolve(null)), [ready, locationId, range.from, range.to]);
  const refresh = () => Promise.all([summary.reload(), handovers.reload()]);

  const rows = useMemo(() => summary.data?.locations ?? [], [summary.data]);
  const totals = useMemo(() => rows.reduce((a, r) => ({
    sales: a.sales + r.sales_total, collected: a.collected + r.collected, handovers: a.handovers + r.handovers + r.handover_reversals, expected: a.expected + r.expected_now,
  }), { sales: 0, collected: 0, handovers: 0, expected: 0 }), [rows]);

  // Entrega de efectivo (local / bodega) y base de caja (Gerencia).
  const [handoverFor, setHandoverFor] = useState<CashLocation | null>(null);
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [note, setNote] = useState('');
  const [baseFor, setBaseFor] = useState<CashLocation | null>(null);
  /** Local elegido dentro del formulario de base cuando en pantalla hay varios. */
  const [baseLocation, setBaseLocation] = useState<number | ''>('');
  const [reverseFor, setReverseFor] = useState<CashHandover | null>(null);
  const [reason, setReason] = useState('');

  const amountValue = Number(amount) || 0;
  const remaining = (handoverFor?.expected_now ?? 0) - amountValue;

  const applyRange = (f: string, tt: string) => { setFrom(f); setTo(tt); setRange({ from: f, to: tt }); };

  const submitHandover = (e: FormEvent) => {
    e.preventDefault();
    if (!handoverFor) return;
    const scope = `caja:entrega:${handoverFor.location_id}`;
    void run(async () => {
      try {
        const r = await api.createHandover(requestKey(scope), { location_id: handoverFor.location_id, amount: amountValue, recipient: recipient.trim(), note });
        releaseKey(scope);
        setHandoverFor(null); setAmount(''); setRecipient(''); setNote('');
        show(t('cash.done', { doc: r.doc_no, remaining: formatMoney(r.remaining, lang) }));
        await refresh();
      } catch (err) { showError(err); }
    });
  };

  const submitBase = (e: FormEvent) => {
    e.preventDefault();
    const destino = baseLocation === '' ? baseFor?.location_id : Number(baseLocation);
    if (!destino) return;
    const scope = `caja:base:${destino}`;
    void run(async () => {
      try {
        const r = await api.setCashBase(requestKey(scope), { location_id: destino, amount: amountValue, note });
        releaseKey(scope);
        setBaseFor(null); setBaseLocation(''); setAmount(''); setNote('');
        show(t('cash.baseDone', { doc: r.doc_no, expected: formatMoney(r.expected, lang) }));
        await refresh();
      } catch (err) { showError(err); }
    });
  };

  const submitReverse = (e: FormEvent) => {
    e.preventDefault();
    if (!reverseFor) return;
    const scope = `caja:reverso:${reverseFor.document_id}`;
    void run(async () => {
      try {
        const r = await api.reverseDocument(requestKey(scope), reverseFor.document_id, reason);
        releaseKey(scope);
        setReverseFor(null); setReason('');
        show(t('cash.reversed', { doc: r.doc_no }));
        await refresh();
      } catch (err) { showError(err); }
    });
  };

  const single = rows.length === 1 ? rows[0]! : null;
  const canHandover = can('cash.handover');

  return (
    <>
      <div className="actions" id="cashFilters" style={{ marginBottom: 12, alignItems: 'end', flexWrap: 'wrap' }}>
        {seesAll || places.length > 1 ? (
          <div className="field" style={{ minWidth: 200 }}>
            <label htmlFor="cashLocation">{t('cash.locationLabel')}</label>
            <select className="select" id="cashLocation" value={selected} onChange={(e) => setSelected(e.target.value === '' ? '' : Number(e.target.value))}>
              {seesAll ? <option value="">{t('cash.allLocations')}</option> : null}
              {places.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="cashFrom">{t('cash.from')}</label>
          <input className="input" id="cashFrom" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cashTo">{t('cash.to')}</label>
          <input className="input" id="cashTo" type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button type="button" className="btn" id="cashApply" onClick={() => applyRange(from, to)}>{t('cash.apply')}</button>
        <button type="button" className="btn sm" data-range="today" onClick={() => applyRange(today, today)}>{t('cash.today')}</button>
        <button type="button" className="btn sm" data-range="week" onClick={() => applyRange(shiftDay(today, -6), today)}>{t('cash.week')}</button>
        <button type="button" className="btn sm" data-range="month" onClick={() => applyRange(`${today.slice(0, 8)}01`, today)}>{t('cash.month')}</button>
        <span className="help" id="cashRange">{t('cash.showing', { from: formatDate(`${range.from}T12:00:00`, lang, false), to: formatDate(`${range.to}T12:00:00`, lang, false) })}</span>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="lab">{t('cash.kpiExpected')}</div>
          <div className="val" id="cashExpected">{formatMoney(totals.expected, lang)}</div>
          <div className="sub">{t('cash.kpiExpectedSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('cash.kpiSales')}</div>
          <div className="val" id="cashSales">{formatMoney(totals.sales, lang)}</div>
          <div className="sub">{t('cash.kpiSalesSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('cash.kpiCollected')}</div>
          <div className="val" id="cashCollected">{formatMoney(totals.collected, lang)}</div>
          <div className="sub">{t('cash.kpiCollectedSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('cash.kpiHandovers')}</div>
          <div className="val" id="cashHandovers">{formatMoney(Math.abs(totals.handovers), lang)}</div>
          <div className="sub">{t('cash.kpiHandoversSub')}</div>
        </div>
      </div>

      <Panel
        title={t('cash.summaryTitle')}
        description={t('cash.summaryDesc')}
        badge={
          <div className="actions">
            {/* La entrega la registra quien está en esa caja y cash.handover ya va por ubicación: aquí "single" sí corresponde. */}
            {canHandover && single ? (
              <button type="button" className="btn black" id="cashHandoverBtn" onClick={() => { setHandoverFor(single); setAmount(''); setRecipient(''); setNote(''); }}>
                {t('cash.handoverButton')}
              </button>
            ) : null}
            {/* La base se define desde cualquier vista: si hay varias cajas en pantalla, el formulario pregunta cuál. */}
            {can('cash.manage') && rows.length > 0 ? (
              <button type="button" className="btn" id="cashBaseBtn"
                onClick={() => { setBaseFor(single ?? rows[0]!); setBaseLocation(single ? '' : rows[0]!.location_id); setAmount(''); setNote(''); }}>
                {t('cash.baseButton')}
              </button>
            ) : null}
          </div>
        }
      >
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>{t('cash.cols.location')}</th>
                <th className="num">{t('cash.cols.sales')}</th>
                <th className="num">{t('cash.cols.collected')}</th>
                <th className="num">{t('cash.cols.opening')}</th>
                <th className="num">{t('cash.cols.base')}</th>
                <th className="num">{t('cash.cols.cashSales')}</th>
                <th className="num">{t('cash.cols.cashReturns')}</th>
                <th className="num">{t('cash.cols.handovers')}</th>
                <th className="num">{t('cash.cols.closing')}</th>
                <th className="num">{t('cash.cols.expectedNow')}</th>
              </tr>
            </thead>
            <tbody id="cashBody">
              {summary.loading && !summary.data ? <TableSkeleton cols={10} rows={3} /> : null}
              {rows.map((r) => (
                <tr key={r.location_id} data-location={r.code}>
                  <td>
                    <b>{r.name}</b>
                    <div className="tiny muted">
                      {r.by_method.map((m) => `${m.name} ${formatMoney(m.amount, lang)}${m.collected ? '' : ` (${t('cash.notCollected')})`}`).join(' · ') || t('common.noData')}
                    </div>
                  </td>
                  <td className="num" data-col="sales">{formatMoney(r.sales_total, lang)}</td>
                  <td className="num" data-col="collected">{formatMoney(r.collected, lang)}</td>
                  <td className="num">{formatMoney(r.opening, lang)}</td>
                  <td className="num">{formatMoney(r.base, lang)}</td>
                  <td className="num" data-col="cashSales">{formatMoney(r.cash_sales, lang)}</td>
                  <td className="num">{formatMoney(r.cash_returns, lang)}</td>
                  <td className="num" data-col="handovers">{formatMoney(r.handovers + r.handover_reversals, lang)}</td>
                  <td className="num">{formatMoney(r.closing, lang)}</td>
                  <td className="num" data-col="expected"><b>{formatMoney(r.expected_now, lang)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title={t('cash.historyTitle')} description={t('cash.historyDesc')} badge={null}>
        {handovers.data && handovers.data.items.length === 0 ? <EmptyState icon="inbox" title={t('cash.noHandovers')} /> : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>{t('cash.hcols.date')}</th>
                  <th>{t('cash.hcols.doc')}</th>
                  {seesAll ? <th>{t('cash.hcols.location')}</th> : null}
                  <th className="num">{t('cash.hcols.amount')}</th>
                  <th>{t('cash.hcols.recipient')}</th>
                  <th>{t('cash.hcols.user')}</th>
                  <th className="num">{t('cash.hcols.before')}</th>
                  <th>{t('cash.hcols.note')}</th>
                  <th>{t('common.status')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody id="cashHandoverBody">
                {handovers.loading && !handovers.data ? <TableSkeleton cols={seesAll ? 10 : 9} rows={3} /> : null}
                {(handovers.data?.items ?? []).map((h) => (
                  <tr key={h.document_id} data-handover={h.doc_no}>
                    <td>{formatDate(h.created_at, lang)}</td>
                    <td><b>{h.doc_no}</b></td>
                    {seesAll ? <td>{h.location_name}</td> : null}
                    <td className="num">{formatMoney(h.amount, lang)}</td>
                    <td>{h.recipient}</td>
                    <td>{h.user_name ?? ''}</td>
                    <td className="num">{formatMoney(h.expected_before, lang)}</td>
                    <td>{h.note}</td>
                    <td>
                      <Badge tone={h.status === 'REVERSED' ? 'red' : 'green'}>{t(`docStatus.${h.status}`, { defaultValue: h.status })}</Badge>
                      {h.reversed_by_doc_no ? <div className="tiny muted">{h.reversed_by_doc_no}</div> : null}
                    </td>
                    <td>
                      {can('document.reverse') && h.status === 'POSTED' ? (
                        <button type="button" className="btn sm red" data-reverse={h.doc_no} onClick={() => { setReverseFor(h); setReason(''); }}>{t('cash.reverse')}</button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Modal open={!!handoverFor} title={t('cash.handoverTitle', { location: handoverFor?.name ?? '' })} help={t('cash.handoverHelp')} onClose={() => setHandoverFor(null)}>
        <form className="formgrid" id="handoverForm" onSubmit={submitHandover}>
          <div className="field">
            <label htmlFor="hoAmount">{t('cash.amount')}</label>
            <input id="hoAmount" type="number" min={1} step={1} required value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="hoRecipient">{t('cash.recipient')}</label>
            <input id="hoRecipient" required value={recipient} onChange={(e) => setRecipient(e.target.value)} />
          </div>
          <div className="field span2">
            <label htmlFor="hoNote">{t('cash.note')}</label>
            <input id="hoNote" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className={remaining < 0 ? 'paystatus bad span2' : 'paystatus span2'} id="hoPreview">
            {amountValue > 0 && remaining < 0
              ? t('cash.previewOver', { expected: formatMoney(handoverFor?.expected_now ?? 0, lang) })
              : t('cash.preview', { amount: formatMoney(amountValue, lang), remaining: formatMoney(Math.max(remaining, 0), lang) })}
          </div>
          <button className="btn black span2" type="submit" id="hoSubmit" disabled={busy || amountValue <= 0 || remaining < 0 || !recipient.trim()}>
            {t('cash.confirm')}
          </button>
        </form>
      </Modal>

      <Modal
        open={!!baseFor}
        title={single ? t('cash.baseTitle', { location: baseFor?.name ?? '' }) : t('cash.baseTitleAny')}
        help={t('cash.baseHelp')}
        onClose={() => { setBaseFor(null); setBaseLocation(''); }}
      >
        <form className="formgrid" id="baseForm" onSubmit={submitBase}>
          {!single ? (
            <div className="field span2">
              <label htmlFor="baseLocation">{t('cash.baseLocation')}</label>
              <select id="baseLocation" required value={baseLocation} onChange={(e) => setBaseLocation(e.target.value === '' ? '' : Number(e.target.value))}>
                {rows.map((r) => <option key={r.location_id} value={r.location_id}>{r.name}</option>)}
              </select>
            </div>
          ) : null}
          <div className="field">
            <label htmlFor="baseAmount">{t('cash.baseAmount')}</label>
            <input id="baseAmount" type="number" min={1} step={1} required value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="baseNote">{t('cash.note')}</label>
            <input id="baseNote" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <button className="btn black span2" type="submit" disabled={busy || amountValue <= 0}>{t('common.save')}</button>
        </form>
      </Modal>

      <Modal open={!!reverseFor} title={t('cash.reverseTitle', { doc: reverseFor?.doc_no ?? '' })} help={t('cash.reverseHelp')} onClose={() => setReverseFor(null)}>
        <form className="formgrid" id="cashReverseForm" onSubmit={submitReverse}>
          <div className="field span2">
            <label htmlFor="cashReverseReason">{t('cash.reason')}</label>
            <input id="cashReverseReason" required value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <button className="btn black span2" type="submit" disabled={busy || !reason.trim()}>{t('cash.reverse')}</button>
        </form>
      </Modal>
    </>
  );
}
